import React from 'react';
import { useQuery } from 'react-query';
import { ChartData, dataExistsChecker, handleSales } from '../utils/reusable-func';
import api from '../../../../components/api';

function useRenderChartRoute(currentRoute: {value: number, label: string} | null) {
  /* Handle current view of chart
    * 0. Today
    * 1. This Week
    * 2. This Month
    * 3. 3 Months
    * 4. 6 Months
    * 5. 1 Year
    */
  const [currentView, setCurrentView] = React.useState<number>(0);

  /*
    * Get Chart Data
    * Need to be run with enabled after finish fetching first data for handling error from Back End
    */
  // 1. Today Data
  const { data: today, isLoading: todayLoad } = useQuery(
    ['hourly', 'route', 'analytics', currentRoute?.value, 1],
    () => api.analytics.route.hourly(currentRoute!.value, 1),
    {
      enabled: !!currentRoute,
    },
  );
    // 2. Week Data
  const { data: week, isLoading: weekLoad } = useQuery(
    ['hourly', 'route', 'analytics', currentRoute?.value, 7],
    () => api.analytics.route.hourly(currentRoute!.value, 7),
    {
      enabled: !!today && !!currentRoute,
    },
  );
    // 3. Month Data
  const { data: month, isLoading: monthLoad } = useQuery(
    ['daily', 'route', 'analytics', currentRoute?.value, 30],
    () => api.analytics.route.daily(currentRoute!.value, 30),
    {
      enabled: !!week && !!currentRoute,
    },
  );
    // 4. Three Month Data
  const { data: threeMonth, isLoading: threeMonthLoad } = useQuery(
    ['daily', 'route', 'analytics', currentRoute?.value, 90],
    () => api.analytics.route.daily(currentRoute!.value, 90),
    {
      enabled: !!month && !!currentRoute,
    },
  );
    // 5. Six Month Data
  const { data: sixMonth, isLoading: sixMonthLoad } = useQuery(
    ['weekly', 'route', 'analytics', currentRoute?.value, 6],
    () => api.analytics.route.weekly(currentRoute!.value, 6),
    {
      enabled: !!threeMonth && !!currentRoute,
    },
  );
    // 6. Year Data
  const { data: year, isLoading: yearLoad } = useQuery(
    ['monthly', 'route', 'analytics', currentRoute?.value, 12],
    () => api.analytics.route.monthly(currentRoute!.value, 12),
    {
      enabled: !!sixMonth && !!currentRoute,
    },
  );

  // Data Management
  const routeSales: ChartData | undefined = React.useMemo(() => handleSales({
    today: today?.data?.data,
    oneWeek: week?.data?.data,
    oneMonth: month?.data?.data,
    threeMonth: threeMonth?.data?.data,
    sixMonth: sixMonth?.data?.data,
    year: year?.data?.data,
  }, currentView), [year, currentView]);

  // Checking data exists or not for rendering chart
  const isDataExists: boolean = React.useMemo(() => dataExistsChecker({
    today: today?.data?.data,
    oneWeek: week?.data?.data,
    oneMonth: month?.data?.data,
    threeMonth: threeMonth?.data?.data,
    sixMonth: sixMonth?.data?.data,
    year: year?.data?.data,
  }), [year]);

  // Checking API Loading status
  const isLoading: boolean = React.useMemo(
    () => todayLoad || weekLoad || monthLoad || threeMonthLoad || sixMonthLoad || yearLoad,
    [todayLoad, weekLoad, monthLoad, threeMonthLoad, sixMonthLoad, yearLoad],
  );

  return {
    currentView,
    setCurrentView,
    routeSales,
    isLoading,
    isDataExists,
  };
}

export default useRenderChartRoute;
